import { Row, Col, Select, Table, Input } from "antd";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import styled, { createGlobalStyle } from "styled-components";
import { currencySymbolMap } from "common/Constants";

// ─── Global Style ─────────────────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled.div`
  background: #fff;
  padding: 28px 32px;
  min-height: 100vh;
`;

const SectionCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const FormLabel = styled.div`
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 0;
  font-size: 14px;
  .required { color: #e53935; }
`;

const StyledInput = styled(Input)`
  background: #f5f5f5 !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 4px !important;
  height: 40px !important;
  color: #1a1a1a !important;
  width: 100%;
  font-size: 14px !important;
  &::placeholder { color: #aaa !important; }
  &:focus, &:hover {
    border-color: #b2b2b2 !important;
    box-shadow: none !important;
  }
`;

const StyledSelect = styled(Select)`
  width: 100% !important;
  .ant-select-selector {
    background: #f5f5f5 !important;
    border: 1px solid #e0e0e0 !important;
    border-radius: 4px !important;
    height: 40px !important;
    align-items: center !important;
    font-size: 14px !important;
  }
  .ant-select-selection-item {
    color: #1a1a1a !important;
    line-height: 40px !important;
  }
  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: #b2b2b2 !important;
    box-shadow: none !important;
  }
`;

const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #e0e0e0;
  padding: 12px 16px;
`;

const SummaryHeaderText = styled.span`
  font-weight: 700;
  font-size: 15px;
  color: #1a1a1a;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eeeeee;
  &:last-child { border-bottom: none; }
`;

const SummaryLabel = styled.span`
  font-weight: ${({ bold }) => (bold ? "700" : "400")};
  color: #1a1a1a;
  font-size: 15px;
`;

const SummaryValue = styled.span`
  font-weight: ${({ bold }) => (bold ? "700" : "400")};
  color: #1a1a1a;
  font-size: 15px;
`;

const StyledTable = styled(Table)`
  .ant-table {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    overflow: hidden;
  }
  .ant-table-thead > tr > th {
    font-weight: 600;
    font-size: 14px;
    background: #f5f6fa !important;
    color: #3b4a6b !important;
    border-bottom: 1px solid #e0e0e0 !important;
    padding: 14px 16px !important;
  }
  .ant-table-tbody > tr > td {
    font-size: 14px;
    color: #1a1a1a;
    border-bottom: 1px solid #f0f0f0 !important;
    padding: 14px 16px !important;
  }
  .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
  .ant-table-tbody > tr:hover > td      { background: #fafafa !important; }
  .ant-table-placeholder .ant-table-cell { border-bottom: none !important; }
`;

const BlueLink = styled.span`color: #1677ff; cursor: pointer; font-size: 14px;`;
const BlueValue = styled.span`color: #1677ff; font-size: 14px;`;

const TotalBar = styled.div`
  font-weight: 700;
  font-size: 15px;
  color: #1a1a1a;
  margin-bottom: 12px;
`;

const FormRowStyled = styled(Row)`
  margin-bottom: 6px;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (val, fallback = "—") =>
	(val !== undefined && val !== null && val !== "") ? String(val) : fallback;

const fmtMoney = (val, currencyCode = "USD") => {
	const num = parseFloat(val);
	if (isNaN(num)) return "$0";
	const symbol = CURRENCY_SYMBOLS[currencyCode?.toUpperCase()] || currencyCode || "$";
	return `${symbol}${num.toLocaleString()}`;
};

const fmtUSD = (val) =>
	val !== undefined && val !== null && val !== ""
		? `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
		: `$0.00`;

// ─── Currency options & reverse-map ──────────────────────────────────────────
const CURRENCY_OPTIONS = [
	{ label: "US Dollar", value: "usd" },
	{ label: "Australian Dollar", value: "aud" },
	{ label: "Euro", value: "eur" },
	{ label: "Japanese Yen", value: "jpy" },
	{ label: "Kenyan Shilling", value: "kes" },
	{ label: "Indian Rupee", value: "inr" },
	{ label: "British Pound", value: "gbp" },
];

const CURRENCY_TO_VALUE = {
	"US Dollar": "usd",
	"Australian Dollar": "aud",
	"Euro": "eur",
	"Japanese Yen": "jpy",
	"Kenyan Shilling": "kes",
	"Indian Rupee": "inr",
	"British Pound": "gbp",

	USD: "usd",
	AUD: "aud",
	EUR: "eur",
	JPY: "jpy",
	KES: "kes",
	INR: "inr",
	GBP: "gbp",
};

const CURRENCY_SYMBOLS = {
	USD: "$",
	KES: "KSh",
	GBP: "£",
	EUR: "€",
	AUD: "A$",
	INR: "₹",
	CAD: "C$",
	ZAR: "R",
	NGN: "₦",
	GHS: "₵",
	UGX: "USh",
	TZS: "TSh",
	JPY: "¥",
};



// ─── Table Column Definitions ─────────────────────────────────────────────────
const resourceColumns = [
	{
		title: "Name",
		dataIndex: "name",
		key: "name",
		render: (t) => <BlueLink>{t}</BlueLink>,
	},
	{ title: "Role", dataIndex: "role", key: "role" },
	{ title: "Start Date", dataIndex: "startDate", key: "startDate" },
	{ title: "Finish Date", dataIndex: "finishDate", key: "finishDate" },
	{
		title: "Mandays",
		dataIndex: "mandays",
		key: "mandays",
		render: (t) => <BlueValue>{t}</BlueValue>,
	},
	{
		title: "Charge out / hr",
		dataIndex: "chargeout",
		key: "chargeout",
		render: (t) => <BlueValue>{t}</BlueValue>,
	},
	{
		title: "Total Booked Hours",
		dataIndex: "bookedHours",
		key: "bookedHours",
		render: (t) => <BlueValue>{t}</BlueValue>,
	},
	{
		title: "Total PCO",
		dataIndex: "totalPco",
		key: "totalPco",
		render: (t) => <BlueValue>{t}</BlueValue>,
	},
	{ title: "Status", dataIndex: "status", key: "status" },
	{ title: "Action", dataIndex: "action", key: "action" },
];

const subcontractorColumns = [
	{ title: "Subcontractor", dataIndex: "subcontractor", key: "subcontractor" },
	{ title: "Description", dataIndex: "description", key: "description" },
	{ title: "Currency", dataIndex: "currency", key: "currency" },
	{ title: "Amount", dataIndex: "amount", key: "amount" },
	{ title: "Reference No", dataIndex: "referenceNo", key: "referenceNo" },
	{ title: "Remark", dataIndex: "remark", key: "remark" },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function FinancialSummary({ matterData }) {
	const fin = matterData?.financial_resourcePlan || {};
	const currencySymbol =
		CURRENCY_SYMBOLS[(fin.currency || "USD").toUpperCase()] || "$";
	// ── 2. Form field values ─────────────────────────────────────────────────
	const financialYear = fmt(fin.financialYear, "2025");
	const mandays = fmt(fin.mandays, "0");
	const localCurrencyAmount = fmt(
		fin.localCurrencyAmount ?? fin.billingAmount ?? fin.billing_amount,
		"0"
	);
	const conversionRate = fmt(fin.conversionRate, "1");
	const usdAmount = fmt(fin.usdAmount, "0");
	const pfaStatus = fmt(fin.pfaStatus, fmt(matterData?.pfaStatus, "Not Applicable"));
	const billingAmount =
		parseFloat(fin.localCurrencyAmount) ||
		parseFloat(fin.billingAmount) ||
		parseFloat(fin.billing_amount) || 0;

	const billingAmountUsd =
		parseFloat(fin.usdAmount) ||
		parseFloat(fin.usd_amount) ||
		parseFloat(fin.billingAmount) || 0;
	const currency = fin.currency || "US Dollar";
	const currencySelectVal = CURRENCY_TO_VALUE[currency] || "usd";

	// ── 3. Resource list ─────────────────────────────────────────────────────
	// ✅ FIX: viewmatter returns lowercase keys — employeename, projectrole, personday
	const rawResources = matterData?.resourceList || [];

	const resourceData = rawResources.map((r, i) => {
		const rate = parseFloat(r.resourceExtRate ?? r.chargeout) || 0;
		const mandays = parseFloat(r.personday ?? r.personDay) || 0;

		return {
			key: String(i),
			resourceId: r.resourceId || null,
			name: r.employeename || r.employeeName || "—",
			role: r.managerStatus || r.projectrole || r.projectRole || "—",
			startDate: r.startDate
				? dayjs(r.startDate).format("DD-MM-YYYY")
				: "—",

			finishDate: (r.finishDate || r.endDate)
				? dayjs(r.finishDate || r.endDate).format("DD-MM-YYYY")
				: "—",

			mandays: fmt(mandays, "0"),

			chargeout: fmtMoney(rate),

			bookedHours: fmt(
				r.bookedHours ??
				r.totalBookedHours ??
				mandays * 8,
				"0.00"
			),

			totalPco: fmtMoney(rate * mandays * 8),

			status: r.status || "Assigned",

			action: "",
		};
	});

	// ── 4. Subcontract list ──────────────────────────────────────────────────
	const rawSubcontracts = matterData?.subcontractList || [];

	const subcontractData = rawSubcontracts.map((s, i) => ({
		key: String(i),
		subcontractor: fmt(s.subcontractor),
		description: fmt(s.description),
		currency: fmt(s.currency),
		currencycode: fmt(s.currencycode),
		amount: s.amount != null ? fmtMoney(s.amount, s.currencycode) : "$0",
		referenceNo: fmt(s.referenceNo),
		remark: fmt(s.remark),
	}));
	
	// ── 5. Summary card computed values ─────────────────────────────────────
	const totalSubcontractor = (matterData?.subcontractList || [])?.reduce((sum, item) => sum + item.amount * parseFloat(item.currencyrate), 0);

	const totalChargeout = rawResources.reduce((sum, r) => {
		const rate = parseFloat(r.resourceExtRate ?? r.chargeout) || 0;
		const mandays = parseFloat(r.personday ?? r.personDay) || 0;

		return sum + (rate * mandays * 8);
	}, 0);

	const estimatedProfit = billingAmountUsd - totalSubcontractor - totalChargeout;
	const pfaUsdAmount = estimatedProfit < 0 ? Math.abs(estimatedProfit) : 0;
	const profitAfterPfa = estimatedProfit > 0 ? estimatedProfit : 0;

	const summaryItems = [
		{ label: "Total Mandays", value: fmt(fin.mandays, "0"), bold: false },
		{ label: "Billing Amount (USD)", value: fmtMoney(billingAmountUsd), bold: false },
		{ label: "Planned Chargeout", value: fmtMoney(totalChargeout.toFixed(2)), bold: false },
		{ label: "Planned Subcontractor", value: fmtUSD(totalSubcontractor), bold: false },
		{ label: "Estimated Profit", value: fmtMoney(estimatedProfit.toFixed(2)), bold: true },
		{ label: "PFA (Planned Fee Adjustment)", value: estimatedProfit < 0 ? fmtMoney(Math.abs(estimatedProfit.toFixed(2))) : "$0", bold: false },
		{ label: "Profit – after PFA", value: estimatedProfit > 0 ? fmtMoney(estimatedProfit.toFixed(2)) : "$0", bold: false },
	];

	// ── Render ────────────────────────────────────────────────────────────────
	return (
		<>
			<GlobalStyle />
			<PageWrapper>

				{/* ── TOP SECTION: Form + Summary ── */}
				<Row gutter={32} style={{ marginBottom: 24 }}>

					{/* LEFT — Financial plan fields */}
					<Col xs={24} lg={13}>
						<FormRowStyled gutter={[16, 4]} align="middle">
							<Col span={8}>
								<FormLabel>
									Billing Entity <span className="required">*</span>
								</FormLabel>
							</Col>

							<Col span={16}>
								<StyledInput
									value={fmt(
										fin.billingOffice ??
										fin.billing_office ??
										matterData?.financial_resourcePlan?.billingEntity,
										"—"
									)}
									readOnly
								/>
							</Col>
						</FormRowStyled>
						<FormRowStyled gutter={[16, 4]} align="middle">
							<Col span={8}>
								<FormLabel>Financial Year <span className="required">*</span></FormLabel>
							</Col>
							<Col span={16}>
								<StyledInput value={financialYear} readOnly />
							</Col>
						</FormRowStyled>

						<FormRowStyled gutter={[16, 4]} align="middle">
							<Col span={8}>
								<FormLabel>Mandays</FormLabel>
							</Col>
							<Col span={16}>
								<StyledInput value={mandays} readOnly />
							</Col>
						</FormRowStyled>

						<FormRowStyled gutter={[16, 4]} align="middle">
							<Col span={8}>
								<FormLabel>Currency</FormLabel>
							</Col>
							<Col span={16}>
								<StyledSelect value={currencySelectVal} disabled>
									{CURRENCY_OPTIONS.map((c) => (
										<Select.Option key={c.value} value={c.value}>{c.label}</Select.Option>
									))}
								</StyledSelect>
							</Col>
						</FormRowStyled>

						<FormRowStyled gutter={[16, 4]} align="middle">
							<Col span={8}>
								<FormLabel>Local Currency Amount</FormLabel>
							</Col>
							<Col span={16}>
								<StyledInput value={localCurrencyAmount} prefix={currencySymbol} placeholder="—" readOnly />
							</Col>
						</FormRowStyled>

						<FormRowStyled gutter={[16, 4]} align="middle">
							<Col span={8}>
								<FormLabel>Conversion Rate</FormLabel>
							</Col>
							<Col span={16}>
								<StyledInput value={conversionRate} readOnly />
							</Col>
						</FormRowStyled>

						<FormRowStyled gutter={[16, 4]} align="middle">
							<Col span={8}>
								<FormLabel>USD Amount</FormLabel>
							</Col>
							<Col span={16}>
								<StyledInput value={usdAmount} readOnly />
							</Col>
						</FormRowStyled>

						<FormRowStyled gutter={[16, 4]} align="middle">
							<Col span={8}>
								<FormLabel>PFA Status</FormLabel>
							</Col>
							<Col span={16}>
								<StyledInput value={pfaStatus} readOnly />
							</Col>
						</FormRowStyled>

					</Col>

					{/* RIGHT — Summary card */}
					<Col xs={24} lg={11}>
						<SectionCard>
							<SummaryHeader>
								<SummaryHeaderText>Summary</SummaryHeaderText>
								<SummaryHeaderText>Budget (USD)</SummaryHeaderText>
							</SummaryHeader>
							{summaryItems.map((item, i) => (
								<SummaryRow key={i}>
									<SummaryLabel bold={item.bold}>{item.label}</SummaryLabel>
									<SummaryValue bold={item.bold}>{item.value}</SummaryValue>
								</SummaryRow>
							))}
						</SectionCard>
					</Col>

				</Row>

				{/* ── RESOURCE TABLE ── */}
				<Row style={{ marginBottom: 28 }}>
					<Col span={24}>
						<StyledTable
							columns={resourceColumns}
							dataSource={resourceData}
							pagination={false}
							size="middle"
							locale={{ emptyText: "No resources assigned" }}
						/>
					</Col>
				</Row>

				{/* ── SUBCONTRACT TABLE ── */}
				<Row>
					<Col span={24}>
						<TotalBar>
							TOTAL PLANNED CHARGEOUT: {fmtMoney(totalChargeout)}
						</TotalBar>
						<StyledTable
							columns={subcontractorColumns}
							dataSource={subcontractData}
							pagination={false}
							size="middle"
							locale={{ emptyText: "No subcontractors" }}
						/>
					</Col>
				</Row>

			</PageWrapper>
		</>
	);
}